import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Watchlist, WatchlistCompanyAddRequest, BenchmarkPortfolio, IBenchmarkCompany, PortfolioType } from '../../monitoring.model';
import { ICompany } from '../../../../core/models/company.model';
import { MatDialog } from '@angular/material/dialog';
import { CompanySearchPopupComponent } from '../company-search-popup/company-search-popup.component';
import { MonitoringService } from '../../monitoring.service';
import { BenchmarkService } from '../../benchmark.service';
import { IWatchlistCompany } from '../../monitoring.model';
import { ThematicSearchModule } from 'src/app/features/thematic-search/thematic-search.module';
import { WatchlistStore } from '../../store/watchlist.store';
import { WatchlistCompaniesTableComponent } from '../watchlist-companies-table/watchlist-companies-table.component';
import { CsvUploadComponent } from '../csv-upload/csv-upload.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-watchlist-main-page',
  standalone: true,
  imports: [CommonModule, WatchlistCompaniesTableComponent],
  templateUrl: './watchlist-main-page.component.html',
  styleUrl: './watchlist-main-page.component.css'
})
export class WatchlistMainPageComponent implements OnInit, OnDestroy {
  @Input() userWatchlists: Watchlist[] = [];
  @Input() benchmarks: BenchmarkPortfolio[] = [];
  /** Emitted after any action that changes watchlist companies */
  @Output() watchlistUpdated = new EventEmitter<void>();
  /** Ask container to open create dialog */
  @Output() createWatchlist = new EventEmitter<void>();
  /** Ask container to open edit dialog for a watchlist */
  @Output() editWatchlist = new EventEmitter<Watchlist>();
  watchlistCompanies: (IWatchlistCompany | IBenchmarkCompany)[] = [];
  loading: boolean = false;
  selectedWatchlist: Watchlist | null = null;
  selectedBenchmark: BenchmarkPortfolio | null = null;
  activeSection: PortfolioType = 'watchlist';
  isReadOnly: boolean = false;
  private subscriptions: any[] = [];

  constructor(
    private dialog: MatDialog,
    private monitoringService: MonitoringService,
    private benchmarkService: BenchmarkService,
    private snackBar: MatSnackBar,
    private watchlistStore: WatchlistStore
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userWatchlists'] && changes['userWatchlists'].currentValue) {
      // Watchlists have been updated, check if we need to load companies
      const activeWatchlist = this.watchlistStore.getCurrentActiveWatchlist();
      if (activeWatchlist && this.userWatchlists.some(w => w.watchlist_id === activeWatchlist.watchlist_id)) {
        this.loadWatchlistCompanies();
      }
    }
  }

  ngOnInit(): void {
    // Subscribe to store changes
    this.subscriptions.push(
      this.watchlistStore.activeWatchlist$.subscribe(watchlist => {
        // Handle null case (when watchlist is deleted)
        if (!watchlist) {
          this.selectedWatchlist = null;
          if (this.activeSection === 'watchlist') {
            this.watchlistCompanies = [];
            this.loading = false;
          }
          return;
        }
        
        // Only load if watchlist actually changed
        if (watchlist.watchlist_id !== this.selectedWatchlist?.watchlist_id) {
          this.selectedWatchlist = watchlist;
          this.selectedBenchmark = null;
          this.isReadOnly = false;
          this.loadWatchlistCompanies();
        } else {
          // Just update the reference without reloading
          this.selectedWatchlist = watchlist;
        }
      })
    );

    this.subscriptions.push(
      this.watchlistStore.activeBenchmark$.subscribe(benchmark => {
        // Handle null case
        if (!benchmark) {
          this.selectedBenchmark = null;
          if (this.activeSection === 'benchmark') {
            this.watchlistCompanies = [];
            this.loading = false;
          }
          return;
        }
        
        // Only load if benchmark actually changed
        if (benchmark.benchmark_id !== this.selectedBenchmark?.benchmark_id) {
          this.selectedBenchmark = benchmark;
          this.selectedWatchlist = null;
          this.isReadOnly = true;
          this.loadBenchmarkCompanies();
        } else {
          // Just update the reference without reloading
          this.selectedBenchmark = benchmark;
        }
      })
    );

    this.subscriptions.push(
      this.watchlistStore.activeSection$.subscribe(section => {
        this.activeSection = section;
        this.isReadOnly = section === 'benchmark';
      })
    );
  }
  
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => {
      if (sub) {
        sub.unsubscribe();
      }
    });
  }


  onCreateWatchlist() {
    this.createWatchlist.emit();
  }

  onEditWatchlist(watchlist: Watchlist) {
    this.editWatchlist.emit(watchlist);
  }

  onAddCompanies() {
    if (!this.selectedWatchlist) {
      this.snackBar.open('Please select a watchlist first', 'Close', { duration: 3000 });
      return;
    }
    const updatedWatchlist = { ...this.selectedWatchlist, company_count: this.watchlistCompanies.length };

    const dialogRef = this.dialog.open(CompanySearchPopupComponent, {
      // width: '1000px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      data: { watchlist: updatedWatchlist }
    });
    dialogRef.afterClosed().subscribe(() => {
      // this.loadWatchlistCompanies();
      this.onWatchlistUpdated();
    });
  }

  /** Fetch companies for current active watchlist; no-op if none/invalid */
  loadWatchlistCompanies() {
    const activeWatchlist = this.watchlistStore.getCurrentActiveWatchlist();
    if (!activeWatchlist) {
      this.watchlistCompanies = [];
      return;
    }

    //make sure the watchliost is in th user watchlists
    if (!this.userWatchlists.some(watchlist => watchlist.watchlist_id === activeWatchlist.watchlist_id)) {
      this.watchlistCompanies = [];
      return;
    }
    
    const watchlistId = activeWatchlist.watchlist_id;
  
    
    // Set loading state for this watchlist
    this.watchlistStore.setWatchlistLoading(watchlistId, true);
    this.loading = true;
    this.watchlistCompanies = [];
    
    this.monitoringService.getWatchlistCompanies(watchlistId).subscribe({
      next: (res) => {

        if (res.status === 204) {
          this.watchlistCompanies = [];
          this.loading = false;
          this.watchlistStore.setWatchlistLoading(watchlistId, false);
          return;
        }
        // Only display if response is for current active watchlist
        const currentActive = this.watchlistStore.getCurrentActiveWatchlist();
        
        if (currentActive?.watchlist_id === watchlistId) {
          this.watchlistCompanies = res.body?.payload || [];
          this.loading = false;
          this.watchlistStore.setWatchlistLoading(watchlistId, false);
        } else {
          this.watchlistCompanies = [];
          this.watchlistStore.setWatchlistLoading(watchlistId, false);
          this.loading = false;
          return;
        }
     
      }, 
      error: (error) => {
        alert('Failed to load companies!');
        this.loading = false;
        this.watchlistStore.setWatchlistLoading(watchlistId, false);
      }
    });
  }



  onUploadPortfolio() {
    if (!this.selectedWatchlist) {
      this.snackBar.open('Please select a watchlist first', 'Close', { duration: 3000 });
      return;
    }

    // Pass updated watchlist data with current company count
    const updatedWatchlist = { ...this.selectedWatchlist, company_count: this.watchlistCompanies.length };
    
    const dialogRef = this.dialog.open(CsvUploadComponent, {
      width: '1200px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      data: { watchlist: updatedWatchlist }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.companies?.length > 0) {
        this.addCompaniesToWatchlist(result.companies);
      }
    });
  }

  private addCompaniesToWatchlist(companies: any[]) {
    if (!this.selectedWatchlist) return;

    this.loading = true;
    this.monitoringService.addCompaniesToWatchlist(this.selectedWatchlist.watchlist_id, companies).subscribe({
      next: (response) => {
        this.loading = false;
        this.snackBar.open(`Successfully added ${companies.length} companies to watchlist`, 'Close', { duration: 3000 });
        // update the watchlist company count
        this.selectedWatchlist!.company_count+=companies.length;
        this.watchlistStore.setActiveWatchlist(this.selectedWatchlist);
        this.onWatchlistUpdated();
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.open('Error adding companies: ' + error.message, 'Close', { duration: 5000 });
      }
    });
  }

  /** Fetch companies for current active benchmark; no-op if none/invalid */
  loadBenchmarkCompanies() {
    const activeBenchmark = this.watchlistStore.getCurrentActiveBenchmark();
    if (!activeBenchmark) {
      this.watchlistCompanies = [];
      this.loading=false;
      return;
    }

   
    const benchmarkId = activeBenchmark.benchmark_id;
    this.watchlistStore.setBenchmarkLoading(benchmarkId, true);
    this.loading = true;
    this.watchlistCompanies = [];
    
    this.benchmarkService.getBenchmarkCompanies(benchmarkId).subscribe({
      next: (res) => {
        const currentActive = this.watchlistStore.getCurrentActiveBenchmark();
        if (currentActive?.benchmark_id !== benchmarkId) {
          this.watchlistStore.setBenchmarkLoading(benchmarkId, false);
          this.loading = false;
          return;
        }
  
        if (res.status === 204) {
          this.watchlistCompanies = [];
        } else {
          this.watchlistCompanies = res.body?.payload || [];
        }
  
        this.loading = false;
        this.watchlistStore.setBenchmarkLoading(benchmarkId, false);
      },
      error: () => {
        this.loading = false;
        this.watchlistStore.setBenchmarkLoading(benchmarkId, false);
      }
    });
  }

  onWatchlistUpdated() {
    const section = this.watchlistStore.getCurrentActiveSection();
    if (section === 'watchlist') {
      this.loadWatchlistCompanies();
    } else {
      this.loadBenchmarkCompanies();
    }
    this.watchlistUpdated.emit();
  }

  getCurrentPortfolioName(): string {
    if (this.selectedWatchlist) {
      return this.selectedWatchlist.watchlist_name;
    } else if (this.selectedBenchmark) {
      return this.selectedBenchmark.benchmark_name;
    }
    return '';
  }
}